import { Component, EventEmitter, inject, Input, OnInit, Output, Signal } from '@angular/core';
import { ExerciseTemplateDto } from '@therapist/shared/models/exercise/exercise-template.dto';
import { FormArray, FormBuilder, FormControl, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { TherapyPlanExerciseDto } from '@therapist/shared/models/exercise/therapy-plan-exercise.dto';
import { ExerciseTemplateEditListEntryItemComponent } from '../exercise-template-edit-list-entry-item/exercise-template-edit-list-entry-item.component';
import { RepetitionUnitTypeEnum } from '@therapist/shared/models/exercise/repetition-unit-type.enum';

@Component({
  selector: 'sana-tp-exercise-entry-list-wrapper',
  imports: [
    ReactiveFormsModule,
    ExerciseTemplateEditListEntryItemComponent
  ],
  templateUrl: './exercise-template-edit-list-wrapper.component.html',
  styleUrl: './exercise-template-edit-list-wrapper.component.scss'
})
export class ExerciseTemplateEditListWrapperComponent implements OnInit {

  @Input() templateExerciseList!: Signal<ExerciseTemplateDto[]>;
  @Input() therapyPlanExerciseList!: TherapyPlanExerciseDto[];

  @Input({ required: true }) clientId!: string;

  @Output() removeExercise = new EventEmitter<string>();
  @Output() formValueChanged = new EventEmitter<TherapyPlanExerciseDto[]>();
  @Output() formStatusChanged = new EventEmitter<boolean>();

  readonly #fb = inject(FormBuilder);

  form = this.#fb
    .nonNullable
    .group<ExerciseWithTrainingSetFormArrayModel>({ exercises: this.#fb.nonNullable.array<ExerciseWithTrainingSetFormModel>([]) });

  constructor() {
    this.form
      .valueChanges
      .pipe(
        takeUntilDestroyed(),
        distinctUntilChanged(),
        debounceTime(300),
        filter(_ => this.form.valid)
      )
      .subscribe(() => this.formValueChanged.emit(this.form.getRawValue().exercises as unknown as TherapyPlanExerciseDto[]));

    this.form
      .statusChanges
      .pipe(takeUntilDestroyed())
      .subscribe(status => this.formStatusChanged.emit(status === 'VALID'));
  }

  ngOnInit() {
    this.templateExerciseList().forEach(exc => this.addNewFormControlFromTemplateExercise(exc));
    this.form.enable({ emitEvent: false });
  }

  private addNewFormControlFromTemplateExercise(exc: ExerciseTemplateDto): void {
    (this.form.controls.exercises as FormArray).push(
      this.#fb.nonNullable.group<ExerciseWithTrainingSetFormModel>(this.addNewFormGroupFromTemplateExercise(exc), { updateOn: 'change' }));
  }

  private addNewFormGroupFromTemplateExercise(exc: ExerciseTemplateDto): ExerciseWithTrainingSetFormModel {
    return <ExerciseWithTrainingSetFormModel>{
      templateId: new FormControl<string>(exc.id),
      numberOfSets: new FormControl<number>(
        this.createDefaultFormOptions<number>(3, true),
        this.createDefaultValidators()
      ),
      numberOfRepetitions: new FormControl<number>(
        this.createDefaultFormOptions<number>(exc.repetitionUnitType === RepetitionUnitTypeEnum.Execution ? 15 : 30, true),
        this.createDefaultValidators()
      ),
      customInstructions: new FormControl<string>(
        this.createDefaultFormOptions<string>('', true),
        [
          Validators.maxLength(255)
        ]
      )
    };
  }

  private createDefaultFormOptions<T>(value: T, disabled: boolean = false): { value: T, disabled: boolean } {
    return { value: value, disabled: disabled };
  }

  private createDefaultValidators(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.min(1)
    ];
  }

  removeFromTherapyPlan(templateId: string, index: number): void {
    (this.form.controls.exercises as FormArray).removeAt(index);
    this.removeExercise.emit(templateId);
  }

  getFormArray(): FormArray<FormControl<ExerciseWithTrainingSetFormModel>> {
    return this.form.controls.exercises as FormArray<FormControl<ExerciseWithTrainingSetFormModel>>;
  }
}

export interface ExerciseWithTrainingSetFormArrayModel {
  exercises: FormArray<FormControl<ExerciseWithTrainingSetFormModel>>;
}

export interface ExerciseWithTrainingSetFormModel {
  templateId: FormControl<string>;
  numberOfSets: FormControl<number>;
  numberOfRepetitions: FormControl<number>;
  customInstructions: FormControl<string | null>;
}

